import { useCallback, useEffect, useState } from "react";
import { Eye, SlidersHorizontal, Building2 } from "lucide-react";
import { useEngagement } from "../hooks/useEngagement";
import { ErrorBanner } from "../components/ErrorBanner";

/** Operator token shared with the Admin page (sessionStorage). */
const SK_TOKEN = "levier.admin.token";
const DEFAULT_ENGAGEMENT = "levier-ia-internal";

interface IcpFilter {
  naf?: string[];
  employees_min?: number;
  employees_max?: number;
  region?: string;
  country?: string;
}
interface Effective {
  icpFilter: IcpFilter;
  enrichmentBudget: number;
  coldAuditThreshold: number;
  coldAuditDailyCap: number;
}
interface IcpView {
  engagement_id: string;
  effective: Effective;
}
interface PharowCompany {
  id: string;
  name: string;
  naf?: string;
  employees_range?: string;
  website?: string;
}
interface PreviewResult {
  pharow_unbound: boolean;
  count: number;
  sample: PharowCompany[];
  limit: number;
}
interface Stage {
  key: string;
  label: string;
  count: number;
  drop_off_from_previous: number;
}

const inputStyle = {
  background: "var(--color-bg-card)",
  color: "var(--color-text)",
  border: "1px solid var(--color-border)",
} as const;

export function Prospects() {
  const { engagementId } = useEngagement();
  const target = engagementId || DEFAULT_ENGAGEMENT;
  const usingDefault = !engagementId;

  // ── ICP config ──────────────────────────────────────────────────────────
  const [naf, setNaf] = useState("");
  const [empMin, setEmpMin] = useState("");
  const [empMax, setEmpMax] = useState("");
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [budget, setBudget] = useState("");
  const [threshold, setThreshold] = useState("");
  const [effective, setEffective] = useState<Effective | null>(null);
  const [cfgLoading, setCfgLoading] = useState(true);
  const [cfgError, setCfgError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const applyView = useCallback((view: IcpView) => {
    const f = view.effective.icpFilter ?? {};
    setNaf((f.naf ?? []).join(", "));
    setEmpMin(f.employees_min != null ? String(f.employees_min) : "");
    setEmpMax(f.employees_max != null ? String(f.employees_max) : "");
    setCountry(f.country ?? "");
    setRegion(f.region ?? "");
    setBudget(String(view.effective.enrichmentBudget));
    setThreshold(String(view.effective.coldAuditThreshold));
    setEffective(view.effective);
  }, []);

  const loadCfg = useCallback(
    (signal?: AbortSignal) => {
      setCfgLoading(true);
      setCfgError(null);
      setSavedAt(null);
      fetch(`/api/engagements/${encodeURIComponent(target)}/icp`, { signal })
        .then(async (res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status} — ${(await res.text()).slice(0, 140)}`);
          return res.json() as Promise<IcpView>;
        })
        .then((view) => {
          applyView(view);
          setCfgLoading(false);
        })
        .catch((err: Error) => {
          if (err.name === "AbortError") return;
          setCfgError(err.message);
          setCfgLoading(false);
        });
    },
    [target, applyView],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    loadCfg(ctrl.signal);
    return () => ctrl.abort();
  }, [loadCfg]);

  const nafArr = naf.split(",").map((s) => s.trim()).filter(Boolean);
  const canSave = nafArr.length > 0 && !saving;

  const save = async () => {
    setSaving(true);
    setCfgError(null);
    setSavedAt(null);
    const icp_filter: IcpFilter = { naf: nafArr };
    if (empMin) icp_filter.employees_min = Number(empMin);
    if (empMax) icp_filter.employees_max = Number(empMax);
    if (country) icp_filter.country = country.trim();
    if (region) icp_filter.region = region.trim();
    const body: Record<string, unknown> = { icp_filter };
    if (budget) body.enrichment_budget = Number(budget);
    if (threshold) body.cold_audit_threshold = Number(threshold);
    try {
      const res = await fetch(`/api/engagements/${encodeURIComponent(target)}/icp`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status} — ${(await res.text()).slice(0, 160)}`);
      applyView((await res.json()) as IcpView);
      setSavedAt(Date.now());
    } catch (err) {
      setCfgError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  // ── Pharow preview (credit-free, operator-gated) ─────────────────────────
  const [token, setToken] = useState(() =>
    typeof window !== "undefined" ? window.sessionStorage.getItem(SK_TOKEN) ?? "" : "",
  );
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const runPreview = async () => {
    if (!token) {
      setPreviewError("Token opérateur requis (le même que la page Admin).");
      return;
    }
    window.sessionStorage.setItem(SK_TOKEN, token);
    setPreviewing(true);
    setPreviewError(null);
    try {
      const res = await fetch(`/api/pharow/preview-search`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ engagement_id: target }),
      });
      if (res.status === 401) throw new Error("Token opérateur invalide (401).");
      if (!res.ok) throw new Error(`HTTP ${res.status} — ${(await res.text()).slice(0, 160)}`);
      setPreview((await res.json()) as PreviewResult);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : String(err));
    } finally {
      setPreviewing(false);
    }
  };

  // ── Outbound funnel (existing endpoint) ──────────────────────────────────
  const [funnel, setFunnel] = useState<Stage[] | null>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    fetch(`/api/metrics/outbound-funnel?engagement=${encodeURIComponent(target)}`, { signal: ctrl.signal })
      .then((res) => (res.ok ? (res.json() as Promise<{ stages: Stage[] }>) : null))
      .then((json) => json && setFunnel(json.stages))
      .catch(() => {});
    return () => ctrl.abort();
  }, [target]);

  return (
    <div className="space-y-8 lg:space-y-10">
      <header className="reveal">
        <p className="eyebrow">Console · 03</p>
        <h2 className="font-display italic text-3xl sm:text-4xl lg:text-[56px] leading-[0.95] mt-2 tracking-tight">
          <span className="text-[var(--color-text)]">Cockpit </span>
          <span className="text-accent-gradient">Pharow</span>
        </h2>
        <p className="mt-3 text-sm text-[var(--color-text-muted)] max-w-2xl">
          Pilotez le ciblage ICP et les plafonds de sourcing par engagement, et prévisualisez les sociétés Pharow
          <span className="text-[var(--color-text)]"> sans dépenser de crédit</span> avant d'activer l'enrichissement.
        </p>
        <p className="mt-2 font-mono text-[11px] text-[var(--color-text-dim)]">
          Engagement : <span className="text-[var(--color-accent)]">{target}</span>
          {usingDefault && " (par défaut — sélectionnez un engagement pour en cibler un autre)"}
        </p>
      </header>

      {cfgError && <ErrorBanner message={cfgError} onRetry={loadCfg} context="config ICP" />}

      {/* ── ICP editor ──────────────────────────────────────────────────── */}
      <section className="panel reveal p-5 sm:p-6 space-y-5" style={{ animationDelay: "80ms" }}>
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="size-4 text-[var(--color-accent)]" strokeWidth={1.5} />
          <p className="eyebrow">// Ciblage ICP</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="space-y-1.5 sm:col-span-2">
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">
              Codes NAF (séparés par des virgules) — requis
            </span>
            <input
              value={naf}
              onChange={(e) => setNaf(e.target.value)}
              placeholder="70.22Z, 73.11Z"
              disabled={cfgLoading}
              className="w-full font-mono text-xs px-3 py-2 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
              style={inputStyle}
            />
          </label>
          <Field label="Effectif min" value={empMin} onChange={setEmpMin} disabled={cfgLoading} type="number" />
          <Field label="Effectif max" value={empMax} onChange={setEmpMax} disabled={cfgLoading} type="number" />
          <Field label="Pays" value={country} onChange={setCountry} disabled={cfgLoading} placeholder="FR" />
          <Field label="Région" value={region} onChange={setRegion} disabled={cfgLoading} placeholder="Île-de-France" />
          <Field label="Budget enrichissement / tick" value={budget} onChange={setBudget} disabled={cfgLoading} type="number" />
          <Field label="Seuil BANT cold-audit" value={threshold} onChange={setThreshold} disabled={cfgLoading} type="number" />
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button
            onClick={save}
            disabled={!canSave}
            className="font-mono text-xs px-4 py-2 rounded disabled:opacity-40 transition-opacity"
            style={{ background: "var(--color-accent)", color: "white" }}
          >
            {saving ? "Enregistrement…" : "Enregistrer le ciblage"}
          </button>
          {nafArr.length === 0 && (
            <span className="font-mono text-[10px] text-[var(--color-status-error)]">Au moins un code NAF requis.</span>
          )}
          {savedAt && (
            <span className="font-mono text-[10px] text-[var(--color-status-active)]">✓ Enregistré</span>
          )}
          {effective && (
            <span className="font-mono text-[10px] text-[var(--color-text-dim)] ml-auto">
              Cap quotidien d'audits (global) : <span className="text-[var(--color-text-muted)]">{effective.coldAuditDailyCap}</span>
            </span>
          )}
        </div>
      </section>

      {/* ── Pharow preview ──────────────────────────────────────────────── */}
      <section className="panel reveal p-5 sm:p-6 space-y-4" style={{ animationDelay: "160ms" }}>
        <div className="flex items-center gap-2">
          <Eye className="size-4 text-[var(--color-accent)]" strokeWidth={1.5} />
          <p className="eyebrow">// Aperçu Pharow (sans crédit)</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Token opérateur"
            className="font-mono text-xs px-3 py-2 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
            style={{ ...inputStyle, minWidth: "200px" }}
          />
          <button
            onClick={runPreview}
            disabled={previewing}
            className="font-mono text-xs px-4 py-2 rounded disabled:opacity-40 transition-opacity"
            style={{ background: "var(--color-accent-soft)", color: "var(--color-accent)", border: "1px solid var(--color-accent)" }}
          >
            {previewing ? "Recherche…" : "Aperçu"}
          </button>
          {preview && !preview.pharow_unbound && (
            <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
              {preview.count} société(s) — échantillon (max {preview.limit})
            </span>
          )}
        </div>

        {previewError && <p className="font-mono text-[11px] text-[var(--color-status-error)]">{previewError}</p>}

        {preview?.pharow_unbound && (
          <p className="font-mono text-[11px] text-[var(--color-text-dim)]">
            Pharow non connecté — ajoutez <span className="text-[var(--color-text-muted)]">PHAROW_API_KEY</span> pour prévisualiser de vrais résultats.
          </p>
        )}

        {preview && !preview.pharow_unbound && preview.sample.length > 0 && (
          <div className="space-y-2">
            {preview.sample.map((c) => (
              <div key={c.id} className="flex items-center gap-3 panel p-3" style={{ background: "var(--color-bg-elev)" }}>
                <Building2 className="size-4 text-[var(--color-text-dim)]" strokeWidth={1.5} />
                <span className="text-sm text-[var(--color-text)] flex-1 truncate">{c.name}</span>
                {c.naf && <span className="pill text-[var(--color-text-muted)]">{c.naf}</span>}
                {c.employees_range && (
                  <span className="font-mono text-[10px] text-[var(--color-text-dim)]">{c.employees_range}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Outbound funnel (existing metric) ───────────────────────────── */}
      <section className="space-y-4">
        <div className="section-rule">
          <p className="eyebrow">// Funnel outbound (30 j)</p>
        </div>
        {funnel ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {funnel.map((s) => (
              <div key={s.key} className="panel p-4">
                <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">{s.label}</p>
                <p className="font-display italic text-3xl mt-1 tabular-nums text-[var(--color-text)]">{s.count}</p>
                {s.drop_off_from_previous > 0 && (
                  <p className="font-mono text-[10px] text-[var(--color-text-muted)] mt-1">
                    −{Math.round(s.drop_off_from_previous * 100)}% vs étape préc.
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="font-mono text-[11px] text-[var(--color-text-dim)]">Chargement du funnel…</p>
        )}
      </section>
    </div>
  );
}

function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className="space-y-1.5">
      <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">{props.label}</span>
      <input
        type={props.type ?? "text"}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        disabled={props.disabled}
        className="w-full font-mono text-xs px-3 py-2 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
        style={inputStyle}
      />
    </label>
  );
}
